import React, { useCallback, useEffect, useState } from "react";
import ModalComponent from "../../../components/modals";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Grid } from "@mui/material";
import FormTextField from "../../../components/textField/formTextField";
import {
  callActionApiAsync,
  getBiomedicalCatalogItemSummary,
  postAddBiomedicalCatalogItem,
  putUpdateBiomedicalCatalogItem
} from "../../../services/apiService";
import ToastNotification from "../../../components/toastNotification";

const validationSchema = yup.object().shape({
  name: yup.string().required("Este campo es obligatorio")
});

const CatalogItemModal = ({
  open,
  close,
  mode,
  itemRow,
  userId,
  catalogDef,
  onSaved
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "warning",
    summary: "",
    detail: ""
  });
  const [usageSummary, setUsageSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: "" }
  });

  const isEdit = mode === "edit";

  const loadEquipmentCount = useCallback(async () => {
    const itemId = itemRow?.value;
    if (!isEdit || itemId == null || itemId === "" || !userId) {
      setUsageSummary(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const { data } = await getBiomedicalCatalogItemSummary(
        catalogDef.catalogType,
        itemId,
        userId
      );
      setUsageSummary({
        totalCount:
          typeof data?.count === "number" ? data.count : data?.equipment?.length ?? 0,
        equipmentCount:
          typeof data?.equipmentCount === "number"
            ? data.equipmentCount
            : data?.equipment?.length ?? 0,
        templatesCount:
          typeof data?.templatesCount === "number"
            ? data.templatesCount
            : data?.templates?.length ?? 0
      });
    } catch {
      setUsageSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [catalogDef.catalogType, isEdit, itemRow?.value, userId]);

  useEffect(() => {
    if (open) {
      reset({ name: isEdit ? itemRow?.name || "" : "" });
      loadEquipmentCount();
    } else {
      reset({ name: "" });
      setUsageSummary(null);
    }
  }, [open, reset, isEdit, itemRow?.name, loadEquipmentCount]);

  const handleClose = () => {
    reset({ name: "" });
    setUsageSummary(null);
    close();
  };

  const onSubmit = async (formData) => {
    const payload = {
      userId,
      catalogType: catalogDef.catalogType,
      name: formData.name
    };

    if (isEdit) {
      payload.id = itemRow?.value;
    }

    await callActionApiAsync(
      () =>
        isEdit
          ? putUpdateBiomedicalCatalogItem(payload)
          : postAddBiomedicalCatalogItem(payload),
      () => {
        onSaved?.();
        handleClose();
      },
      (error) => {
        setToastConfig({
          severity: "error",
          summary: isEdit ? "Error de edición" : "Error de creación",
          detail:
            typeof error === "string" && error
              ? error
              : `No se pudo ${isEdit ? "actualizar" : "crear"} ${catalogDef.labelArticle}.`
        });
        setShowToast(true);
      }
    );
  };

  const title = isEdit
    ? `Editar ${catalogDef.labelSingular}`
    : catalogDef.createTitle || `Nuevo ${catalogDef.labelSingular}`;

  const buttonText = isEdit ? "Guardar" : `Agregar ${catalogDef.labelSingular}`;

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title={title}
        actions={<div className="HContainer j-center w-complete"></div>}
        width="100%"
        maxWidth="640px"
      >
        <div className="HContainer">
          <form
            className="VContainer align-center m-20 w-complete"
            onSubmit={handleSubmit(onSubmit)}
          >
            {isEdit && summaryLoading && (
              <p className="mb-12" style={{ fontSize: "14px", color: "#666" }}>
                Comprobando referencias vinculadas…
              </p>
            )}
            {isEdit &&
              !summaryLoading &&
              usageSummary != null &&
              usageSummary.totalCount > 0 && (
              <div
                className="mb-15"
                style={{
                  padding: "10px 12px",
                  fontSize: "14px",
                  lineHeight: 1.45,
                  background: "#e3f2fd",
                  borderRadius: "8px",
                  border: "1px solid #90caf9",
                  maxWidth: "640px"
                }}
                role="status"
              >
                Está modificando {catalogDef.labelArticle} con{" "}
                <strong>{usageSummary.totalCount}</strong> referencia(s)
                {usageSummary.equipmentCount > 0
                  ? ` en ${usageSummary.equipmentCount} equipo(s)`
                  : ""}
                {usageSummary.templatesCount > 0
                  ? ` y ${usageSummary.templatesCount} ${catalogDef.relatedCountLabel || "plantilla(s) por modelo"}`
                  : ""}
                . El nuevo nombre se reflejará en todos los registros relacionados.
              </div>
              )}
            {isEdit &&
              !summaryLoading &&
              usageSummary != null &&
              usageSummary.totalCount === 0 && (
              <div
                className="mb-15"
                style={{
                  padding: "10px 12px",
                  fontSize: "14px",
                  lineHeight: 1.45,
                  background: "#f5f5f5",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  maxWidth: "640px"
                }}
                role="status"
              >
                {`Este ${catalogDef.labelSingular} no tiene referencias activas en este momento.`}
              </div>
              )}
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <p className="create-label">{catalogDef.fieldLabel}:</p>
                <FormTextField
                  name="name"
                  control={control}
                  error={errors.name}
                />
              </Grid>
            </Grid>
            <LaddaButton
              width={"190px"}
              fontSize={"12px"}
              height={"32px"}
              variant="contained"
              margin={"20px 0"}
              type="submit"
            >
              <p className="button_text">{buttonText}</p>
            </LaddaButton>
          </form>
        </div>
      </ModalComponent>
      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
        position="top-right"
      />
    </>
  );
};

export default CatalogItemModal;
